import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ShoppingCartIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Lens } from './ui/lens';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { useImageSlider } from '../hooks/useImageSlider';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

const QuickViewModal = ({ product, isOpen, onClose }) => {
  const { toggleFavorite, isProductFavorited } = useFavorites();
  const { addToCart } = useCart();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [categoryDiscount, setCategoryDiscount] = useState(null);
  const [flashSaleDiscount, setFlashSaleDiscount] = useState(null);
  const [cartCampaign, setCartCampaign] = useState(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const uniqueImages = useMemo(() => {
    if (!product) return [];
    const allImages = [
      product.main_image_url,
      ...(product.gallery_image_urls || []),
      ...(product.variants?.map(v => v.variant_image_url).filter(Boolean) || [])
    ];
    return [...new Set(allImages)].filter(Boolean);
  }, [product]);

  const { currentIndex, goToIndex } = useImageSlider(uniqueImages, 3500);

  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      const firstAvailableVariant = product.variants.find(v => v.stock > 0) || product.variants[0];
      setSelectedVariant(firstAvailableVariant);
    }
  }, [product]);

  useEffect(() => {
    if (!isOpen || !product) return;

    const checkDiscounts = async () => {
      try {
        // Category discount
        const categoryResponse = await api.post('/api/campaigns/check-unified-category-discounts', {
          cartItems: [{
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            quantity: 1,
            variant: { price_override: null }
          }]
        });

        if (categoryResponse.success && categoryResponse.data.hasDiscounts) {
          const discount = categoryResponse.data.applicableDiscounts[0];
          const affectedItem = discount.affectedItems?.[0];
          setCategoryDiscount({
            campaignName: discount.campaignName,
            badgeText: discount.badgeText,
            badgeColor: discount.badgeColor,
            discountAmount: affectedItem ? affectedItem.discountAmount : discount.totalCampaignDiscount
          });
        }

        // Flash Sale
        const flashResponse = await api.get('/api/flash-sale/active');
        if (flashResponse.success && flashResponse.data?.length > 0) {
          const productFlashSale = flashResponse.data.find(fs =>
            fs.products?.some(p => String(p.id) === String(product.id))
          );
          if (productFlashSale) {
            const productInSale = productFlashSale.products.find(p => p.id === product.id);
            const discountValue = productInSale?.custom_discount_value || productFlashSale.discount_value;
            let discountAmount = 0;
            if (productFlashSale.discount_type === 'percentage') {
              discountAmount = (product.price * discountValue) / 100;
            } else {
              discountAmount = discountValue;
            }
            setFlashSaleDiscount({
              campaignName: productFlashSale.name,
              badgeText: productFlashSale.badge_text || `FLASH %${discountValue}`,
              badgeColor: productFlashSale.badge_color || '#ff4444',
              discountAmount
            });
          }
        }

        // Cart Campaign
        const cartResponse = await api.get('/api/campaigns/active-cart-campaigns');
        if (cartResponse.success && cartResponse.data?.length > 0) {
          const activeCampaigns = cartResponse.data.filter(campaign => {
            const now = new Date();
            const startDate = new Date(campaign.start_date);
            const endDate = new Date(campaign.end_date);
            return campaign.status === 'active' && now >= startDate && now <= endDate;
          });
          if (activeCampaigns.length > 0) {
            const activeCampaign = activeCampaigns[0];
            setCartCampaign({
              id: activeCampaign.id,
              name: activeCampaign.name,
              badgeText: activeCampaign.badge_text || 'SEPET İNDİRİMİ',
              badgeColor: activeCampaign.badge_color || '#10b981',
              discountType: activeCampaign.discount_type,
              discountValue: activeCampaign.discount_value,
              minCartAmount: activeCampaign.min_cart_amount
            });
          }
        }
      } catch (error) {
        console.error('QuickView: Discount check error:', error);
      }
    };

    checkDiscounts();
  }, [isOpen, product]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Swipe to close on mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    // If swiped down more than 150px, close modal
    if (touchStart - touchEnd < -150) {
      onClose();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.error('Lütfen bir seçenek (beden/renk) belirleyin.');
      return;
    }
    if (selectedVariant.stock === 0) {
      toast.error('Seçtiğiniz ürün stokta bulunmamaktadır.');
      return;
    }
    addToCart(product, selectedVariant, 1);
    toast.success('Ürün sepete eklendi!');
  };

  const handlePrevClick = () => {
    const newIndex = (currentIndex - 1 + uniqueImages.length) % uniqueImages.length;
    goToIndex(newIndex);
  };

  const handleNextClick = () => {
    const newIndex = (currentIndex + 1) % uniqueImages.length;
    goToIndex(newIndex);
  };

  if (!isOpen || !product) return null;

  const toNum = (v) => {
    const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
    return isNaN(n) ? 0 : n;
  };

  let originalPrice = toNum(product.original_price);
  let currentPrice = toNum(product.price);

  if (!originalPrice && toNum(product.discount_percentage) > 0) {
    originalPrice = currentPrice / (1 - toNum(product.discount_percentage) / 100);
  }

  if (!originalPrice) {
    originalPrice = currentPrice;
  }

  const categoryDiscountAmount = toNum(categoryDiscount?.discountAmount);
  const flashSaleDiscountAmount = toNum(flashSaleDiscount?.discountAmount);
  const cartDiscountAmount = cartCampaign ? (() => {
    const discountValue = toNum(cartCampaign.discountValue);
    const discountType = cartCampaign.discountType || 'percentage';
    if (discountType === 'percentage') {
      return (currentPrice * discountValue) / 100;
    } else {
      return Math.min(discountValue, currentPrice);
    }
  })() : 0;

  const finalPrice = Math.max(0, currentPrice - categoryDiscountAmount - flashSaleDiscountAmount - cartDiscountAmount);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close Button - Sticky on mobile */}
        <div className="sticky top-0 left-0 right-0 z-50 flex justify-between items-center p-3 bg-white/95 backdrop-blur-sm border-b border-gray-100 md:bg-transparent md:border-0">
          {/* Mobile: Swipe indicator */}
          <div className="md:hidden flex-1 flex justify-center">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="bg-white hover:bg-red-50 rounded-full p-2 shadow-lg transition-all border border-gray-200 hover:border-red-500 group"
          >
            <XMarkIcon className="w-6 h-6 text-gray-700 group-hover:text-red-500" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 md:pt-12">
          {/* Left: Image Gallery */}
          <div className="space-y-4">
            <div className="relative w-full aspect-square overflow-hidden rounded-xl border border-gray-200">
              <Lens
                zoomFactor={2.5}
                lensSize={200}
                isStatic={false}
                ariaLabel="Ürün görseli yakınlaştırma"
              >
                <div
                  className="flex transition-transform duration-700 ease-in-out h-full"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {uniqueImages.map((imgUrl, index) => (
                    <div key={index} className="w-full h-full flex-shrink-0">
                      <img
                        src={imgUrl}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </Lens>

              {uniqueImages.length > 1 && (
                <>
                  <button onClick={handlePrevClick} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white transition z-10">
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button onClick={handleNextClick} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white transition z-10">
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {uniqueImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {uniqueImages.map((imgUrl, index) => (
                  <button
                    key={index}
                    onClick={() => goToIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      currentIndex === index ? 'border-brand-green ring-2 ring-brand-green-light' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={imgUrl} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Details */}
          <div className="space-y-4">
            {/* Category */}
            {product.categories && product.categories.length > 0 && (
              <div className="flex gap-2">
                {product.categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/categories/${category.slug}`}
                    className="text-xs font-medium text-brand-green hover:underline"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Title & Favorite */}
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex-1">{product.name}</h2>
              <button
                onClick={() => toggleFavorite(product)}
                className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition"
              >
                {isProductFavorited(product.id) ? (
                  <HeartIconSolid className="w-6 h-6 text-red-500" />
                ) : (
                  <HeartIconOutline className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-sm text-gray-600">{product.description}</p>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {product.discount_percentage > 0 && (
                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  %{Math.round(product.discount_percentage)} İNDİRİM
                </span>
              )}
              {categoryDiscount && (
                <span
                  className="text-white px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: categoryDiscount.badgeColor }}
                >
                  {categoryDiscount.badgeText}
                </span>
              )}
              {flashSaleDiscount && (
                <span
                  className="text-white px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: flashSaleDiscount.badgeColor }}
                >
                  ⚡ {flashSaleDiscount.badgeText}
                </span>
              )}
              {cartCampaign && (
                <span
                  className="text-white px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: cartCampaign.badgeColor }}
                >
                  🛒 {cartCampaign.badgeText}
                </span>
              )}
            </div>

            {/* Price */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1">
                  <div className="text-red-500 text-3xl sm:text-4xl font-black">
                    ₺{finalPrice.toFixed(2)}
                  </div>
                  {(originalPrice > finalPrice) && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-gray-400 text-lg line-through">
                        ₺{originalPrice.toFixed(2)}
                      </span>
                      <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                        %{Math.round(((originalPrice - finalPrice) / originalPrice) * 100)} İNDİRİM
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Variants */}
            {product.variants && product.variants.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Seçenekler:</h3>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((variant, index) => (
                    <button
                      key={`${variant.color}-${variant.size}-${index}`}
                      type="button"
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-3 py-2 border rounded-lg text-sm font-medium transition-all ${
                        selectedVariant && selectedVariant.color === variant.color && selectedVariant.size === variant.size
                          ? 'bg-brand-green text-white border-brand-green shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-brand-green'
                      } ${variant.stock === 0 ? 'opacity-50 cursor-not-allowed line-through' : ''}`}
                      disabled={variant.stock === 0}
                    >
                      {variant.color} - {variant.size}
                      {variant.stock === 0 && <span className="text-xs block">(Tükendi)</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedVariant || selectedVariant?.stock === 0}
                  className="flex-1 bg-brand-green text-white py-3 rounded-lg font-bold hover:bg-brand-green-light transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ShoppingCartIcon className="w-5 h-5" />
                  Sepete Ekle
                </button>
                <Link
                  to={`/products/${product.id}`}
                  className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition flex items-center justify-center gap-2"
                  onClick={onClose}
                >
                  Detaylara Git
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
              </div>
              
              {/* Mobile: Extra close button at bottom */}
              <button
                onClick={onClose}
                className="md:hidden w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition flex items-center justify-center gap-2"
              >
                <XMarkIcon className="w-5 h-5" />
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
